import React from 'react';
import BarChart from '../../../reports/charts/barChart';
import type { ChartData } from '../../../types/tripsMonitoredUsage';
import type { Granularity } from '../../../types/timeframe';
import { formatTooltipPeriod } from '../utils/formatTooltipPeriod';
import styles from './usageBarChart.module.css';

interface UsageBarChartProps {
  title: string;
  subtitle?: string;
  chartData: ChartData;
  height?: number;
  xAxisLabel?: string;
  dataLabel?: string;
  aggregator?: Granularity;
}

const AGGREGATOR_LABELS: Record<Granularity, string> = {
  daily: 'Daily',
  weekly: 'Weekly',
  monthly: 'Monthly',
  quarterly: 'Quarterly',
  entireRange: 'Entire range',
};

/**
 * Adapter that maps usage-reporting ChartData to the shape expected by
 * the existing BarChart component (reportData.series + reportData.categories).
 */
const UsageBarChart: React.FC<UsageBarChartProps> = ({
  title,
  subtitle,
  chartData,
  height = 300,
  xAxisLabel,
  dataLabel,
  aggregator,
}) => {
  const reportData = {
    series: chartData.series,
    categories: chartData.categories,
  };

  const tooltipFormatter = (stackName: string, value: any, altValue: any, category?: string) => {
    const formattedValue = typeof altValue === 'number'
      ? altValue.toLocaleString('en-US')
      : altValue;
    const categoryIndex = category ? chartData.categories.indexOf(category) : -1;
    const period = categoryIndex >= 0 ? chartData.periods?.[categoryIndex] : undefined;
    const periodLabel =
      formatTooltipPeriod(period, aggregator, chartData.rangeStart, chartData.rangeEnd) ||
      category ||
      '';
    return `${periodLabel}<br/>${formattedValue} ${stackName}`;
  };

  // Legend: "Number Of <data label> <Aggregator> wise" (e.g. "Number Of Trips Monitored Daily wise")
  const baseLabel = dataLabel || (chartData.series[0]?.name ?? 'Data Label');
  const legendLabel = `Number Of ${baseLabel}`;
  // Daily/weekly labels are full dates ("Aug 5, 2026") — keep them on one line instead of wrapping the year underneath.
  const singleLineAxisLabels = aggregator === 'daily' || aggregator === 'weekly';

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <span className={styles.bartitle}>{title}</span>
        {subtitle && <span className={styles.subtitle}>{subtitle}</span>}
      </div>
      <div className={styles.chartWrapper}>
        <BarChart
          reportData={reportData}
          height={height}
          toolTipLabelFunction={tooltipFormatter}
          leftMargin={70}
          highlightOnHover
          centerSingleBar
          yAxisNoThousandsSeparator
          disableClickFilter
          tooltipPosition="right"
          singleLineAxisLabels={singleLineAxisLabels}
          minBarHeight={2}
        />
        <div className={styles.footer}>
          <div className={styles.legend}>
            <span className={styles.legendDot}></span>
            <span className={styles.legendText}>{legendLabel}</span>
          </div>
          {xAxisLabel && <div className={styles.xAxisLabel}>{xAxisLabel}</div>}
        </div>
      </div>
    </div>
  );
};

export default UsageBarChart;
